<template>
  <main-layout
    class="pro-confirm-page"
    :page-title="text.planTitle"
    custom-back
    @listen-back="goBack"
  >
    <div slot="main-content" class="main-content">
      <div class="confirm-card">
        <order-info-card
          :order="order"
          :current-expire-date="currentExpireDate"
          :current-rank-type="currentRankType"
          :server-now="serverNow"
        />

        <div class="pay-panel">
          <pay-qrcode
            :qrcode="qrcode"
            :agreed="agreed"
            :remain-ms="remainMs"
            @toggle-agree="toggleAgree"
            @agree="agreed = true"
            @view-protocol="viewProtocol"
          />
        </div>
      </div>

      <pay-dialogs
        :confirmVisible.sync="confirmVisible"
        :queryVisible.sync="queryVisible"
        :failedVisible.sync="failedVisible"
        @cancel="onDialogCancel"
        @paid="onPaid"
        @repay="onRepay"
        @view-order="goOrderList"
      />
    </div>
  </main-layout>
</template>

<script>
import MainLayout from "@/layout/MainLayout";
import OrderInfoCard from "./components/OrderInfoCard.vue";
import PayQrcode from "./components/PayQrcode.vue";
import PayDialogs from "./components/PayDialogs.vue";
import {
  PRO_PLAN_TEXT,
  PAY_POLL_INTERVAL,
  PAY_COUNTDOWN_DEFAULT_SECONDS,
  PAY_METHOD_TEXT,
  PAY_METHOD,
  PAY_RESULT,
  AGREEMENT_TYPE,
  ORDER_STATUS,
  PAY_STATUS_CONFIRM_ATTEMPTS,
  PAY_STATUS_CONFIRM_INTERVAL,
} from "@/consts/proPlan";
import {
  formatDateDash,
  formatDateTimeDash,
  remainMs,
  agreementUrl,
  capPayCountdown,
} from "@/utils/proPlan";
import {
  queryProOrderPayStatus,
  confirmProOrderPaid,
  getProOrderDetail,
  getCompanyPlanStatus,
} from "@/api/request/snkd.proPlan";

export default {
  name: "ProPlanOrderConfirm",
  components: { MainLayout, OrderInfoCard, PayQrcode, PayDialogs },
  data() {
    return {
      text: PRO_PLAN_TEXT,
      order: {},
      // 当前专业版到期日（"yyyy-MM-dd"；免费版为空）——用于按需求规则推算生效/到期日期
      currentExpireDate: "",
      // 企业版本（0免费 / 1专业 / 2旗舰），来自同一资格接口。
      // 生效日规则要区分「付费版未过期 → 到期日+1」与「已过期/免费版 → 当天」（禅道 #23489）
      currentRankType: null,
      // 服务器当前时间（BaseResp.curDate）：倒计时与「生效日期=当天」都以它为准，
      // 规避客户端时钟偏差；接口返回后填充，未填充时退回本地时间
      serverNow: "",
      // 支付二维码内容：来自下单接口 add 返回的 codeUrl
      qrcode: "",
      agreed: false,
      remainMs: 0,
      // 倒计时基准（本地时钟下的绝对截止时间戳）。倒计时据此重算而非逐秒累减，
      // 详见 tickCountdown；为 0 表示当前没有在计时。
      deadlineTs: 0,
      countdownTimer: null,
      pollTimer: null,
      // 是否正在执行「归零后查单」，避免并发重复进入
      expireHandling: false,
      // 倒计时「代次」：每次重新开始计时 +1，用于丢弃归零查单期间已失效的异步结果
      countdownToken: 0,
      confirmVisible: false,
      queryVisible: false,
      failedVisible: false,
    };
  },
  created() {
    this.initOrder();
    this.startCountdown();
    // 支付状态轮询不在此启动：未勾选协议时二维码处于锁定态、用户无法支付，
    // 此时轮询只会产生无意义请求。改由勾选协议后启动（见 watch.agreed）。
  },
  watch: {
    // 勾选「我已阅读并同意」后开始查询订单支付状态
    agreed(val) {
      if (val) this.startPoll();
    },
  },
  mounted() {
    // 回到前台立即校正倒计时（后台期间计时器被节流，见 tickCountdown）
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    this.clearTimers();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  methods: {
    // 订单信息与二维码来自套餐页（add / pending 的返回值经路由带入）
    initOrder() {
      const q = this.$route.query;
      this.qrcode = q.codeUrl || "";
      // 倒计时：以接口截止时间与服务器时间(curDate)计算，规避客户端时钟偏差；
      // 路由未带 curDate 时先用本地时间，随后 fetchOrderDetail 会用接口 curDate 校正
      // capPayCountdown：测试期把后端 30 分钟压到 PAY_COUNTDOWN_MINUTES（见 consts/proPlan.js）
      const remain = remainMs(q.expireTime, this.serverNow);
      this.setRemain(
        isNaN(remain)
          ? PAY_COUNTDOWN_DEFAULT_SECONDS * 1000
          : capPayCountdown(remain)
      );
      this.order = {
        orderId: q.orderId,
        orderNo: q.orderNo,
        companyName: q.companyName || "",
        createTime: q.createTime || Date.now(),
        goodsName: q.planName ? `${this.text.proPrefix}${q.planName}` : "",
        months: Number(q.months) || undefined,
        price: q.price,
        // 默认微信支付；从订单列表等入口进入时会由订单详情补全为接口下发的 payMethodName
        payType: PAY_METHOD_TEXT[PAY_METHOD.WECHAT],
        effectTime: q.effectTime,
        expireTime: q.entitlementEnd || q.expireTime,
      };
      // 始终用订单详情补齐/校正展示字段：
      //  - 路由未带套餐信息时（如从订单列表进入）补 goodsName/months/price；
      //  - 开通企业名称 companyName 路由不带，只能由详情提供；
      //  - 并用接口 curDate 校正倒计时（规避客户端时钟偏差，见 initOrder 注释）
      if (this.order.orderId) {
        this.fetchOrderDetail();
      }
      // 当前专业版到期日：用于推算生效/到期日期，与「立即升级」同源取资格接口
      this.fetchCurrentExpireDate();
    },
    /**
     * 取当前专业版状态（版本 + 到期日；免费版到期日为空）。
     * 生效日期规则（与 App 端 ConfirmOrder 对齐，见 utils/proPlan.calcValidPeriod）：
     *   付费版未过期 → 到期日 + 1 天；已过期 / 免费版 → 当天。
     * 到期日期 = 生效日 + 套餐时长 - 1（加月被月末夹取时不再减 1 天）。
     */
    async fetchCurrentExpireDate() {
      try {
        const res = await getCompanyPlanStatus({
          companyId: this.$route.query.companyId,
        });
        this.currentExpireDate = (res.obj && res.obj.expireDate) || "";
        this.currentRankType =
          res.obj && res.obj.rankType !== undefined
            ? Number(res.obj.rankType)
            : null;
        // 同时记录服务器时间，供「生效日期 = 当天」与倒计时使用
        if (res.curDate) this.serverNow = res.curDate;
      } catch (e) {
        console.log(e);
      }
    },
    // 用订单详情补全信息，并以接口 curDate 校正倒计时
    async fetchOrderDetail() {
      try {
        const res = await getProOrderDetail({
          companyId: this.$route.query.companyId,
          orderId: this.order.orderId,
        });
        if (res.respCode !== 0) return;
        const d = res.obj || {};
        this.order.goodsName = d.goodsName || this.order.goodsName;
        this.order.months = d.months || this.order.months;
        this.order.price = d.price || this.order.price;
        this.order.orderNo = d.orderNo || this.order.orderNo;
        this.order.companyName = d.companyName || this.order.companyName;
        this.order.createTime = d.createTime || this.order.createTime;
        if (d.payType) this.order.payType = d.payType;
        this.order.effectTime = d.effectTime || this.order.effectTime;
        this.order.expireTime = d.expireTime || this.order.expireTime;
        if (res.curDate) this.serverNow = res.curDate;
        // 服务器时间校正倒计时（同样受测试期上限约束）
        const remain = remainMs(this.order.expireTime, res.curDate);
        if (!isNaN(remain)) this.setRemain(capPayCountdown(remain));
      } catch (e) {
        console.log(e);
      }
    },
    /**
     * 写入剩余时长：同时记录绝对截止时间戳。
     * 所有对 remainMs 的赋值都要走这里，否则 deadlineTs 会与新值脱节。
     */
    setRemain(ms) {
      this.remainMs = Math.max(0, isNaN(ms) ? 0 : ms);
      this.deadlineTs = this.remainMs > 0 ? Date.now() + this.remainMs : 0;
    },
    startCountdown() {
      // 幂等：已有计时器时不重复创建（归零查单后重新计时、重新支付都会调用）
      if (this.countdownTimer) return;
      this.countdownToken += 1;
      this.countdownTimer = setInterval(() => this.tickCountdown(), 1000);
    },
    /**
     * 倒计时推进：用「绝对截止时间 − 当前时间」重算，而不是每次减 1000。
     *
     * 背景：标签页切到后台时浏览器会节流 setInterval（Chrome 隐藏页先降到约 1 次/秒，
     * 隐藏约 5 分钟后降到约 1 次/分钟，内嵌 webview 亦同）。累减法下倒计时会严重滞后
     * ——切走 5 分钟再回来仍显示「还有时间」，既不判过期也不查单（禅道 Bug#23437）。
     * 按截止时间重算后，任何一次回调（含回到前台时主动触发的那次）都能立即对齐真实剩余。
     */
    tickCountdown() {
      if (this.expireHandling) return;
      const next = this.deadlineTs
        ? this.deadlineTs - Date.now()
        : this.remainMs;
      this.remainMs = Math.max(0, next);
      if (this.remainMs <= 0) this.handleCountdownEnd();
    },
    // 回到前台：立刻校正一次，不必等被节流的计时器下一次回调
    onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      if (this.countdownTimer) this.tickCountdown();
    },
    /**
     * 倒计时归零：查后端订单状态，再决定去向。
     *
     * 前端倒计时只是「展示值」——后端订单有效期（30 分钟）由后端自行判定超时，前端时钟
     * 与服务器时钟也存在偏差；因此归零 ≠ 订单已过期，不能本地直接跳「订单已过期」。
     * 该查询也不受「同意协议」影响：未勾选协议时后端照样在计时（勾选只影响二维码能否扫）。
     *
     * 兜底重试的原因：后端关单/支付状态可能是异步落库的，归零瞬间首次查单未必已拿到最新状态
     * （或请求本身失败）。故失败、以及「仍是待支付但服务端时间已到」时，按
     * PAY_STATUS_CONFIRM_INTERVAL 间隔重试至多 PAY_STATUS_CONFIRM_ATTEMPTS 次，
     * 仍无结论才按过期处理，避免把还有效的订单误判为过期。
     */
    async handleCountdownEnd() {
      // 归零查单可能耗时数秒；期间 visibilitychange / 计时器回调都会再次触发，
      // 用此开关避免并发重复查单与重复跳转
      if (this.expireHandling) return;
      this.expireHandling = true;
      const token = this.countdownToken;
      this.setRemain(0);
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }

      try {
        for (let i = 0; i < PAY_STATUS_CONFIRM_ATTEMPTS; i++) {
          let res = null;
          try {
            // 必须显式带 companyId：接口层缺省会回落到本地缓存企业，
            // 页面所属企业与缓存不一致时会返回 42002（订单不存在或不属于当前企业）
            res = await queryProOrderPayStatus({
              companyId: this.$route.query.companyId,
              orderId: this.order.orderId,
            });
          } catch (e) {
            console.log(e);
          }
          // 查单期间用户已「重新支付」重新计时 → 本次结果作废，避免误跳
          if (token !== this.countdownToken) return;

          if (res && res.respCode === 0) {
            const d = res.obj || {};
            // 归零前已支付成功（未勾选协议时不会轮询，只能靠这次查单兜住）→ 直接进成功页
            if (d.paid) {
              await this.onPaySuccess(d);
              return;
            }
            if (d.status === ORDER_STATUS.UNPAID) {
              // 剩余时长必须用「支付截止时间」算：待支付订单的 entitlementEnd 为空，
              // 真正的截止时间是下单时下发、经路由带入的 this.order.expireTime（见 initOrder）
              const remain = remainMs(this.order.expireTime, res.curDate);
              const next = isNaN(remain) ? 0 : capPayCountdown(remain);
              if (next > 0) {
                this.setRemain(next);
                this.startCountdown();
                return;
              }
              // 服务端时间也已到、状态却仍是「待支付」→ 后端关单可能还在异步处理，进入下一轮重试
            } else {
              // 已取消 / 已关闭 / 已退款等终态 → 确已过期，无需重试
              break;
            }
          }

          const isLast = i === PAY_STATUS_CONFIRM_ATTEMPTS - 1;
          if (!isLast) {
            await new Promise((r) =>
              setTimeout(r, PAY_STATUS_CONFIRM_INTERVAL)
            );
            if (token !== this.countdownToken) return;
          }
        }

        this.clearTimers();
        this.goExpired();
      } finally {
        this.expireHandling = false;
      }
    },
    /**
     * 每 2 秒轮询支付状态；orderStatus=20 → 支付成功页
     * 幂等：已在轮询时不重复创建计时器（勾选协议与「重新支付」都会触发）
     */
    startPoll() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(async () => {
        if (!this.order.orderId) return;
        const res = await queryProOrderPayStatus({
          companyId: this.$route.query.companyId,
          orderId: this.order.orderId,
        });
        const data = res.obj || {};
        if (data.paid) {
          await this.onPaySuccess(data);
        }
      }, PAY_POLL_INTERVAL);
    },
    // 支付成功统一处理：回填有效期 → 停表 → 跳成功页
    async onPaySuccess(payStatus = {}) {
      this.clearTimers();
      // effectiveTime / entitlementEnd 在支付成功时由接口返回（getPayStatus 或 verifyPayStatus）
      this.order.effectTime = payStatus.effectTime || this.order.effectTime;
      this.order.expireTime = payStatus.expireTime || this.order.expireTime;
      this.goSuccess();
    },
    clearTimers() {
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.countdownTimer = null;
      this.pollTimer = null;
    },
    toggleAgree() {
      this.agreed = !this.agreed;
    },
    viewProtocol() {
      // 复用老端协议页（term_service.html，typeFlag=20 → 专业版服务协议），新窗口打开便于返回继续支付
      window.open(agreementUrl(AGREEMENT_TYPE.PRO_PLAN), "_blank");
    },
    onDialogCancel() {
      this.confirmVisible = false;
    },
    /**
     * 「我已支付」→ 主动查单（verifyPayStatus）
     * payResult 三值：SUCCESS 成功 → 成功页；PROGRESS 处理中 → 提示继续等待，不判失败；
     * FAIL 失败 → 弹「支付失败」。
     */
    async onPaid() {
      this.confirmVisible = false;
      this.queryVisible = true;
      // 保证「正在查询支付结果」有可见时长，避免一闪而过
      const minDelay = new Promise((r) => setTimeout(r, 1200));
      const [res] = await Promise.all([
        confirmProOrderPaid({
          companyId: this.$route.query.companyId,
          orderId: this.order.orderId,
        }),
        minDelay,
      ]);
      this.queryVisible = false;
      if (res.respCode !== 0) {
        this.$message.warning(res.respDesc || this.text.payProgress);
        return;
      }
      const data = res.obj || {};
      if (data.payResult === PAY_RESULT.SUCCESS) {
        await this.onPaySuccess(data);
      } else if (data.payResult === PAY_RESULT.PROGRESS) {
        // 渠道处理中：不判失败，提示后可继续等待或再查
        this.$message.info(this.text.payProgress);
      } else {
        // FAIL 或未返回结果 → 按失败处理
        this.failedVisible = true;
      }
    },
    onRepay() {
      this.failedVisible = false;
      // 重新支付：恢复轮询（订单未过期则继续等待支付结果）
      this.clearTimers();
      if (this.remainMs <= 0) {
        this.setRemain(PAY_COUNTDOWN_DEFAULT_SECONDS * 1000);
        this.startCountdown();
      }
      this.startPoll();
    },
    goSuccess() {
      this.$router.replace({
        path: "/proPlan/orderSuccess",
        query: {
          companyId: this.$route.query.companyId,
          orderId: this.order.orderId,
          orderNo: this.order.orderNo,
          price: this.order.price,
          payType: this.order.payType,
          // 支付成功页按设计稿 1921:867 用 yyyy-mm-dd（下单时间带 hh:mm）
          createTime: formatDateTimeDash(this.order.createTime),
          effectTime: formatDateDash(this.order.effectTime),
          expireTime: formatDateDash(this.order.expireTime),
          months: this.order.months,
          goodsName: this.order.goodsName,
        },
      });
    },
    goExpired() {
      this.$router.replace({
        path: "/proPlan/orderExpired",
        query: {
          companyId: this.$route.query.companyId,
          orderId: this.order.orderId,
        },
      });
    },
    goOrderList() {
      this.$router.push({
        path: "/proPlan/orderList",
        query: { companyId: this.$route.query.companyId },
      });
    },
    goBack() {
      // 用户返回：订单保留，可在订单列表再次支付
      this.clearTimers();
      window.history.length > 1 ? this.$router.back() : window.history.back();
    },
  },
};
</script>

<style lang="scss" scoped>
.main-content {
  min-height: 100%;
  // 顶部留白由卡片 margin 提供，此处不再加 padding-top（禅道 Bug#23429-1）
  padding: 0 0 30px;
  box-sizing: border-box;
  background: #f7f9fb;
}
.confirm-card {
  width: 1100px;
  min-height: 386px;
  // 设计稿：卡片绝对位置 y=109，头部高 80 且叠在其上 → 头部到卡片实际间距 = 109 - 80 = 29px。
  // 本实现里 el-header 是常规流（占 0~80），el-main 从 80 起，故此处只能取差值 29px；
  // 直接写 109px 会多出一整条头部高度（设计稿的 109 是含头部的绝对坐标）。
  margin: 29px auto 0;
  padding: 39px 165px;
  box-sizing: border-box;
  background: #fff;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
}
.pay-panel {
  padding-top: 41px;
}
</style>
